/**
 * Slot Eligibility Utilities
 *
 * Single source of truth for the HARD eligibility filter that decides whether an
 * employee may be placed into a blank slot. This filter runs in TypeScript BEFORE
 * the request reaches the constraint solver: it produces each slot's
 * `allowedEmployeeIds` candidate set.
 *
 * These are NOT the toggleable solver "rules" (SolverOptions: maxCoverage, fairness,
 * skillBalance, balancePreference). Those are SOFT constraints that only steer WHICH
 * eligible employee fills a slot. The functions here decide HOW MANY slots can be
 * filled at all — so disabling the soft rules never raises coverage beyond what this
 * hard filter allows. `evaluateSlotEligibility` returns the FIRST failing reason so
 * the proposal can be explained to the user ("30 blocked: skill missing, 15 blocked:
 * weekly cap, …").
 */

import { isWorkdayInMask, wouldExceedWeeklyCap } from './employment-capacity.utils';
import { doShiftsOverlap } from './shift-overlap.utils';
import { findUnmetSkillRequirements, type EffectiveSkillRatings } from './skill-matching.utils';
import { normaliseSkillEntry, type ShiftSkillEntry } from '../models/shift-rules.model';

/**
 * Why an employee was excluded from a slot. Ordered by evaluation priority — the
 * first matching reason is the one reported (mirrors the short-circuit order).
 */
export type SlotEligibilityReason =
  | 'SHIFT_INVALID_WEEKDAY'
  | 'ALREADY_ASSIGNED'
  | 'UNAVAILABLE'
  | 'WORKDAY_MASK'
  | 'SKILL_MISSING'
  | 'SKILL_RATING_BELOW_MINIMUM'
  | 'SKILL_EXPIRED'
  | 'EMPLOYMENT_INACTIVE'
  | 'WEEKLY_CAP'
  | 'OVERLAP';

/** Reasons in the exact order `evaluateSlotEligibility` checks them. */
export const SLOT_ELIGIBILITY_REASONS: readonly SlotEligibilityReason[] = [
  'SHIFT_INVALID_WEEKDAY',
  'ALREADY_ASSIGNED',
  'UNAVAILABLE',
  'WORKDAY_MASK',
  'SKILL_MISSING',
  'SKILL_RATING_BELOW_MINIMUM',
  'SKILL_EXPIRED',
  'EMPLOYMENT_INACTIVE',
  'WEEKLY_CAP',
  'OVERLAP',
] as const;

/** Minimal employee shape needed for the hard eligibility filter. */
export interface EligibilityEmployee {
  id: string;
  /** Effective skill ratings (skillId -> rating, null = held but unrated) built via buildEffectiveSkillRatings(). */
  skillRatings: EffectiveSkillRatings;
  maxWeeklyHours: number;
  /** Hours already planned for the employee in the relevant window. */
  alreadyPlannedHours: number;
  /** Working-days bitmask (bit 0 = Mon … bit 6 = Sun). */
  workdayMask: number;
  /**
   * ISO expiry date per skill ID, for skills that have one. A skill absent
   * from this map never expires. Checked against the slot's date via
   * `expiredSkillDayKeys` — omit both for template-level solves that have no
   * concrete dates.
   */
  skillExpiryDates?: Record<string, string>;
}

/** Minimal slot/shift shape needed for the hard eligibility filter. */
export interface EligibilitySlotContext {
  /** ISO weekday (0 = Mon … 6 = Sun). */
  weekday: number;
  /** Template/date day key used for overlap + unavailability lookups. */
  dayKey: string;
  shiftUnitId: string;
  /** Required skills as parsed by getRequiredSkillEntries() (plain IDs or entries with minRating). */
  requiredSkills: readonly ShiftSkillEntry[];
  durationHours: number;
  startTime: string;
  endTime: string;
  allowOverlap: boolean;
  /**
   * ISO weekdays this shift is allowed on (0=Mon … 6=Sun). Empty array = unrestricted.
   * When populated and `slot.weekday` is absent from the list, no employee is eligible.
   */
  validWeekdays?: number[];
}

/** An assignment the employee already holds, used for time-overlap checks. */
export interface ExistingAssignment {
  dayKey: string;
  shiftUnitId: string;
  startTime: string;
  endTime: string;
  allowOverlap: boolean;
}

export type SlotEligibilityResult = { eligible: true } | { eligible: false; reason: SlotEligibilityReason };

export interface SlotCandidateOutcome {
  employeeId: string;
  status: 'eligible' | 'blocked';
  reason?: SlotEligibilityReason;
}

/**
 * Decide whether a single employee is eligible for a single slot.
 *
 * Returns `{ eligible: true }` or `{ eligible: false, reason }` where `reason` is the
 * FIRST failing check. The check order (and thus the reported reason) is:
 * SHIFT_INVALID_WEEKDAY → ALREADY_ASSIGNED → UNAVAILABLE → WORKDAY_MASK → SKILL_MISSING →
 * SKILL_RATING_BELOW_MINIMUM → SKILL_EXPIRED → EMPLOYMENT_INACTIVE → WEEKLY_CAP → OVERLAP.
 *
 * @param unavailableDayKeys Optional set of `${dayKey}|${employeeId}` keys marking the
 *   employee unavailable that day (absences, holidays). Omit for template-level solves
 *   that have no concrete dates.
 * @param expiredSkillDayKeys Optional set of `${dayKey}|${employeeId}|${skillId}` keys
 *   marking that a specific skill has expired as of that day. Checked per required
 *   skill so a slot needing a still-valid skill isn't blocked by an unrelated expiry.
 *   Omit for template-level solves that have no concrete dates.
 * @param inactiveEmploymentDayKeys Optional set of `${dayKey}|${employeeId}` keys marking
 *   that the employee has no active employment/team period covering that day. Omit for
 *   template-level solves that have no concrete dates.
 */
export function evaluateSlotEligibility(
  employee: EligibilityEmployee,
  slot: EligibilitySlotContext,
  existingAssignments: ExistingAssignment[],
  unavailableDayKeys?: Set<string>,
  expiredSkillDayKeys?: Set<string>,
  inactiveEmploymentDayKeys?: Set<string>
): SlotEligibilityResult {
  if (slot.validWeekdays && slot.validWeekdays.length > 0 && !slot.validWeekdays.includes(slot.weekday)) {
    return { eligible: false, reason: 'SHIFT_INVALID_WEEKDAY' };
  }
  if (existingAssignments.some(existing => existing.dayKey === slot.dayKey && existing.shiftUnitId === slot.shiftUnitId)) {
    return { eligible: false, reason: 'ALREADY_ASSIGNED' };
  }
  if (unavailableDayKeys?.has(`${slot.dayKey}|${employee.id}`)) {
    return { eligible: false, reason: 'UNAVAILABLE' };
  }
  if (!isWorkdayInMask(employee.workdayMask, slot.weekday)) {
    return { eligible: false, reason: 'WORKDAY_MASK' };
  }
  const unmetSkills = findUnmetSkillRequirements(employee.skillRatings, slot.requiredSkills);
  if (unmetSkills.some(unmet => !unmet.held)) {
    return { eligible: false, reason: 'SKILL_MISSING' };
  }
  if (unmetSkills.length > 0) {
    return { eligible: false, reason: 'SKILL_RATING_BELOW_MINIMUM' };
  }
  if (
    expiredSkillDayKeys &&
    slot.requiredSkills.some(entry => expiredSkillDayKeys.has(`${slot.dayKey}|${employee.id}|${normaliseSkillEntry(entry).skillId}`))
  ) {
    return { eligible: false, reason: 'SKILL_EXPIRED' };
  }
  if (inactiveEmploymentDayKeys?.has(`${slot.dayKey}|${employee.id}`)) {
    return { eligible: false, reason: 'EMPLOYMENT_INACTIVE' };
  }
  if (wouldExceedWeeklyCap(employee.alreadyPlannedHours, slot.durationHours, employee.maxWeeklyHours)) {
    return { eligible: false, reason: 'WEEKLY_CAP' };
  }

  const overlaps = existingAssignments.some(existing => {
    if (existing.dayKey !== slot.dayKey) return false;
    if (slot.allowOverlap || existing.allowOverlap) return false;
    const hasOverlap = doShiftsOverlap(
      { startTime: slot.startTime, endTime: slot.endTime },
      { startTime: existing.startTime, endTime: existing.endTime }
    );
    if (!hasOverlap) return false;
    return true;
  });
  if (overlaps) return { eligible: false, reason: 'OVERLAP' };

  return { eligible: true };
}

/**
 * Aggregated "derivation" of a solver proposal: how the hard filter narrowed the
 * workforce for every blank slot. Lets the UI explain why coverage stops short of
 * 100% even with all soft rules disabled.
 */
export interface SlotEligibilityBreakdown {
  /** Blank slot positions evaluated (one per required headcount gap). */
  slotsEvaluated: number;
  /** Slots with at least one eligible employee (i.e. fillable). */
  slotsWithCandidates: number;
  /** Slots with zero eligible employees (structurally unfillable). */
  slotsWithoutCandidates: number;
  /** Total (employee × slot) checks performed. */
  candidatesEvaluated: number;
  /** Checks that passed (employee was eligible for that slot). */
  candidatesEligible: number;
  /** Rejections grouped by first-failing reason (sums across all slots). */
  blockedByReason: Record<SlotEligibilityReason, number>;
}

/** Fresh, zeroed breakdown accumulator. */
export function createEmptyEligibilityBreakdown(): SlotEligibilityBreakdown {
  return {
    slotsEvaluated: 0,
    slotsWithCandidates: 0,
    slotsWithoutCandidates: 0,
    candidatesEvaluated: 0,
    candidatesEligible: 0,
    blockedByReason: {
      SHIFT_INVALID_WEEKDAY: 0,
      UNAVAILABLE: 0,
      ALREADY_ASSIGNED: 0,
      WORKDAY_MASK: 0,
      SKILL_MISSING: 0,
      SKILL_RATING_BELOW_MINIMUM: 0,
      SKILL_EXPIRED: 0,
      EMPLOYMENT_INACTIVE: 0,
      WEEKLY_CAP: 0,
      OVERLAP: 0,
    },
  };
}

/**
 * Evaluate one blank slot against the whole workforce, returning the eligible
 * employee IDs and updating `breakdown` in place with the reason tally.
 *
 * This is the single call site processors use to build a slot's `allowedEmployeeIds`
 * while simultaneously collecting the derivation data — the boolean decision and the
 * "why" stay in lockstep because they come from the same evaluation.
 */
export function collectEligibleEmployees(
  employees: readonly EligibilityEmployee[],
  slot: EligibilitySlotContext,
  existingByEmployee: Map<string, ExistingAssignment[]>,
  unavailableDayKeys: Set<string> | undefined,
  breakdown: SlotEligibilityBreakdown,
  expiredSkillDayKeys?: Set<string>,
  inactiveEmploymentDayKeys?: Set<string>
): string[] {
  return collectSlotCandidateOutcomes(
    employees,
    slot,
    existingByEmployee,
    unavailableDayKeys,
    breakdown,
    expiredSkillDayKeys,
    inactiveEmploymentDayKeys
  )
    .filter(outcome => outcome.status === 'eligible')
    .map(outcome => outcome.employeeId);
}

export function collectSlotCandidateOutcomes(
  employees: readonly EligibilityEmployee[],
  slot: EligibilitySlotContext,
  existingByEmployee: Map<string, ExistingAssignment[]>,
  unavailableDayKeys: Set<string> | undefined,
  breakdown: SlotEligibilityBreakdown,
  expiredSkillDayKeys?: Set<string>,
  inactiveEmploymentDayKeys?: Set<string>
): SlotCandidateOutcome[] {
  const outcomes: SlotCandidateOutcome[] = [];

  for (const employee of employees) {
    breakdown.candidatesEvaluated++;
    const result = evaluateSlotEligibility(
      employee,
      slot,
      existingByEmployee.get(employee.id) ?? [],
      unavailableDayKeys,
      expiredSkillDayKeys,
      inactiveEmploymentDayKeys
    );
    if (result.eligible) {
      breakdown.candidatesEligible++;
      outcomes.push({ employeeId: employee.id, status: 'eligible' });
    } else {
      breakdown.blockedByReason[result.reason]++;
      outcomes.push({ employeeId: employee.id, status: 'blocked', reason: result.reason });
    }
  }

  breakdown.slotsEvaluated++;
  if (outcomes.some(outcome => outcome.status === 'eligible')) breakdown.slotsWithCandidates++;
  else breakdown.slotsWithoutCandidates++;

  return outcomes;
}

/** Diagnostic-code stub — structurally compatible with the solver `DiagnosticCode`. */
export interface EligibilityDiagnostic {
  code: string;
  metrics: Record<string, number>;
}

/** Maps each eligibility reason to its stable diagnostic/translation code. */
export const ELIGIBILITY_REASON_DIAGNOSTIC_CODE: Record<SlotEligibilityReason, string> = {
  SHIFT_INVALID_WEEKDAY: 'BLOCKED_SHIFT_INVALID_WEEKDAY',
  ALREADY_ASSIGNED: 'BLOCKED_ALREADY_ASSIGNED',
  UNAVAILABLE: 'BLOCKED_UNAVAILABLE',
  WORKDAY_MASK: 'BLOCKED_WORKDAY_MASK',
  SKILL_MISSING: 'BLOCKED_SKILL_MISSING',
  SKILL_RATING_BELOW_MINIMUM: 'BLOCKED_SKILL_RATING_BELOW_MINIMUM',
  SKILL_EXPIRED: 'BLOCKED_SKILL_EXPIRED',
  EMPLOYMENT_INACTIVE: 'BLOCKED_EMPLOYMENT_INACTIVE',
  WEEKLY_CAP: 'BLOCKED_WEEKLY_CAP',
  OVERLAP: 'BLOCKED_OVERLAP',
};

/**
 * Turn a breakdown into user-facing diagnostics that explain the proposal
 * ("Herleitung"): one `BLOCKED_*` entry per non-zero reason plus, when slots stay
 * unfilled, a `HARD_FILTERS_LIMIT_COVERAGE` note clarifying that the coverage ceiling
 * comes from these hard filters — not from the toggleable soft rules — which is why
 * disabling rules does not raise the fill count.
 *
 * @param unfilledSlots Blank slots still open after the solve (deltaSummary.blankSlotsAfter).
 */
export function buildEligibilityDiagnostics(
  breakdown: SlotEligibilityBreakdown | undefined,
  unfilledSlots: number
): EligibilityDiagnostic[] {
  if (!breakdown || breakdown.slotsEvaluated === 0) return [];

  const diagnostics: EligibilityDiagnostic[] = [];

  for (const reason of SLOT_ELIGIBILITY_REASONS) {
    const count = breakdown.blockedByReason[reason];
    if (count > 0) diagnostics.push({ code: ELIGIBILITY_REASON_DIAGNOSTIC_CODE[reason], metrics: { count } });
  }

  if (unfilledSlots > 0 || breakdown.slotsWithoutCandidates > 0) {
    diagnostics.push({
      code: 'HARD_FILTERS_LIMIT_COVERAGE',
      metrics: { unfilled: unfilledSlots, slotsWithoutCandidates: breakdown.slotsWithoutCandidates },
    });
  }

  return diagnostics;
}
